/**
 * Dashboard slice - single source for dashboard API
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/config';
import { DASHBOARD, ACTIVITY_LOGS } from '../../api/endpoints';

export const getOverviewData = createAsyncThunk(
  'dashboard/getOverviewData',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(DASHBOARD.TOTAL);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch dashboard data');
    }
  }
);

export const getRecentActivities = createAsyncThunk(
  'dashboard/getRecentActivities',
  async ({ page = 1, pageSize = 5, search = '' }, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/activity-logs', {
        params: { page, per_page: pageSize, search: search || undefined },
      });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch recent activities');
    }
  }
);

export const getActivityLogById = createAsyncThunk(
  'dashboard/getActivityLogById',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(ACTIVITY_LOGS.BY_ID(id));
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch activity log');
    }
  }
);

const initialState = {
  overview: null,
  recentActivities: null,
  activityLog: null,
  loading: false,
  error: null,
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearDashboardError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOverviewData.fulfilled, (state, action) => {
        state.loading = false;
        state.overview = action.payload;
      })
      .addCase(getRecentActivities.fulfilled, (state, action) => {
        state.loading = false;
        state.recentActivities = action.payload;
      })
      .addCase(getActivityLogById.fulfilled, (state, action) => {
        state.loading = false;
        state.activityLog = action.payload;
      })
      .addMatcher(
        (a) =>
          [getOverviewData.pending, getRecentActivities.pending, getActivityLogById.pending].some(
            (t) => t.match(a)
          ),
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        (a) =>
          [
            getOverviewData.rejected,
            getRecentActivities.rejected,
            getActivityLogById.rejected,
          ].some((t) => t.match(a)),
        (state, action) => {
          state.loading = false;
          state.error = action.payload || 'Something went wrong';
        }
      );
  },
});

export const { clearDashboardError } = dashboardSlice.actions;
export default dashboardSlice.reducer;
